import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Brand } from "@/components/Brand";
import { Check, AlertTriangle, MapPin, Clock, ShieldCheck, FileWarning, ClipboardList, MapPinned } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, Circle } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { PATROL_STATUS_LABEL, SCAN_STATUS_LABEL } from "@/lib/i18n";

const dotIcon = (color) => L.divIcon({
  className: "",
  iconSize: [22, 22],
  iconAnchor: [11, 11],
  html: `<div style="width:22px;height:22px;background:${color};border:3px solid #FFFBF4;border-radius:50%;box-shadow:0 0 0 1px ${color}"></div>`,
});

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function VerifyPatrol() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${BASE}/api/public/verify/patrol/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "No pudimos verificar este reporte."));
  }, [token]);

  if (err) return <PublicShell><div className="card" style={{ borderColor: "#BF3437", padding: 32, textAlign: "center" }}><AlertTriangle size={36} style={{ color: "#BF3437", marginBottom: 8 }}/><div style={{ fontWeight: 800, fontSize: 18, color: "#0A0A0A" }}>{err}</div></div></PublicShell>;
  if (!data) return <PublicShell><div className="skel" style={{ height: 200 }} /></PublicShell>;

  const p = data.patrol;
  const center = data.points.length ? [data.points[0].latitude, data.points[0].longitude] : [-34.6037, -58.3816];
  const geoScans = data.scans.filter((s) => s.latitude != null && s.longitude != null);
  const scanLine = geoScans.map((s) => [s.latitude, s.longitude]);
  const gpsLine = (data.track || []).map((t) => [t.latitude, t.longitude]);

  return (
    <PublicShell>
      <VerifiedBanner issued_at={data.issued_at} expires_at={data.expires_at} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }} className="max-md:!grid-cols-1">
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "#BF3437", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Reporte de ronda</div>
          <h1 style={{ fontSize: 32, fontWeight: 900, color: "#0A0A0A", letterSpacing: "-0.02em", margin: "0 0 6px" }}>{data.client.name}</h1>
          <div style={{ color: "var(--invict-text-muted)", fontSize: 13 }}>{data.client.address || "—"}</div>
        </div>
        <div className="card" style={{ padding: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12 }}>
            <div><div style={{ fontSize: 9, color: "var(--invict-text-muted)", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>Programada</div><div style={{ fontWeight: 700, color: "#0A0A0A" }}>{new Date(p.scheduled_for).toLocaleString("es-AR")}</div></div>
            <div><div style={{ fontSize: 9, color: "var(--invict-text-muted)", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>Vigilador</div><div style={{ fontWeight: 700, color: "#0A0A0A" }}>{p.vigilador_name}</div></div>
            <div><div style={{ fontSize: 9, color: "var(--invict-text-muted)", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>Iniciada</div><div style={{ fontWeight: 700, color: "#0A0A0A" }}>{p.started_at ? new Date(p.started_at).toLocaleString("es-AR") : "—"}</div></div>
            <div><div style={{ fontSize: 9, color: "var(--invict-text-muted)", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>Completada</div><div style={{ fontWeight: 700, color: "#0A0A0A" }}>{p.completed_at ? new Date(p.completed_at).toLocaleString("es-AR") : "—"}</div></div>
            <div><div style={{ fontSize: 9, color: "var(--invict-text-muted)", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>Estado</div><div style={{ fontWeight: 700, color: "#0A0A0A", textTransform: "uppercase" }}>{PATROL_STATUS_LABEL[p.status] || p.status}</div></div>
            <div><div style={{ fontSize: 9, color: "var(--invict-text-muted)", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>Progreso</div><div style={{ fontWeight: 700, color: "#0A0A0A" }}>{p.points_completed} / {p.total_points}{p.anomalies > 0 && <span style={{ color: "#BF3437" }}> · {p.anomalies} anomalía(s)</span>}</div></div>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden", height: 420, marginBottom: 16 }}>
        <MapContainer center={center} zoom={17} style={{ height: "100%", width: "100%" }}>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {data.points.map((pt) => (
            <Marker key={pt.id} position={[pt.latitude, pt.longitude]} icon={dotIcon("#0A0A0A")}>
              <Popup><b>{pt.name}</b><br/>Punto #{pt.order}</Popup>
            </Marker>
          ))}
          {data.points.map((pt) => (
            <Circle key={`r-${pt.id}`} center={[pt.latitude, pt.longitude]} radius={pt.radius_meters} pathOptions={{ color: "#0A0A0A", fillColor: "#0A0A0A", fillOpacity: 0.05, weight: 1, dashArray: "4 4" }} />
          ))}
          {geoScans.map((s) => (
            <Marker key={s.id} position={[s.latitude, s.longitude]} icon={dotIcon("#BF3437")}>
              <Popup><b>{s.patrol_point_name}</b><br/>{new Date(s.scanned_at).toLocaleString("es-AR")}<br/>Distancia: {s.distance_meters}m</Popup>
            </Marker>
          ))}
          {scanLine.length > 1 && <Polyline positions={scanLine} pathOptions={{ color: "#BF3437", weight: 3 }} />}
          {gpsLine.length > 1 && <Polyline positions={gpsLine} pathOptions={{ color: "#2563eb", weight: 3, dashArray: "2 6" }} />}
        </MapContainer>
      </div>

      {gpsLine.length > 1 && (
        <div style={{ display: "flex", gap: 16, fontSize: 11, color: "var(--invict-text-muted)", marginBottom: 16, marginTop: -8 }} data-testid="verify-gps-legend">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 3, background: "#2563eb", display: "inline-block" }} /> Recorrido GPS caminado durante la ronda</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 3, background: "#BF3437", display: "inline-block" }} /> Puntos escaneados en orden</span>
        </div>
      )}

      <h2 style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0A0A0A", margin: "0 0 10px" }}>Escaneos verificados ({data.scans.length})</h2>
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {data.scans.length === 0 && <div style={{ padding: 24, textAlign: "center", color: "var(--invict-text-muted)" }}>Sin escaneos registrados.</div>}
        {data.scans.map((s) => (
          <div key={s.id} style={{ display: "flex", padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)", gap: 12, alignItems: "center" }} data-testid={`verify-scan-${s.id}`}>
            <div style={{ width: 30, height: 30, borderRadius: "50%", background: s.status === "ok" ? "#16a34a" : "#BF3437", color: "white", display: "grid", placeItems: "center", flexShrink: 0 }}>{s.status === "ok" ? <Check size={14}/> : <AlertTriangle size={14}/>}</div>
            <div style={{ flex: 1, fontSize: 13 }}>
              <div style={{ fontWeight: 800, color: "#0A0A0A" }}>{s.patrol_point_name}</div>
              <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>
                {new Date(s.scanned_at).toLocaleString("es-AR")} · {s.gps_degraded ? "verificado por foto/video (sin señal GPS)" : `${s.distance_meters}m del punto`} · {SCAN_STATUS_LABEL[s.status] || s.status}{s.offline_sync ? " · sincronizado sin conexión" : ""}
              </div>
              {s.attachments?.length > 0 && (
                <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }} data-testid={`verify-scan-evidence-${s.id}`}>
                  {s.attachments.map((a, i) => (
                    <a key={i} href={a.url?.startsWith("http") ? a.url : `${BASE}${a.url}`} target="_blank" rel="noreferrer" style={{ width: 56, height: 56, borderRadius: 3, overflow: "hidden", background: "#000", display: "block" }}>
                      {a.kind === "image" ? <img src={a.url?.startsWith("http") ? a.url : `${BASE}${a.url}`} alt="evidencia" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ color: "#FFFBF4", fontSize: 8, textAlign: "center", paddingTop: 20 }}>VIDEO</div>}
                    </a>
                  ))}
                </div>
              )}
            </div>
            <MapPin size={14} style={{ color: "var(--invict-text-muted)" }}/>
          </div>
        ))}
      </div>
    </PublicShell>
  );
}

export function VerifyPeriod() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${BASE}/api/public/verify/period/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "No pudimos verificar este reporte."));
  }, [token]);

  if (err) return <PublicShell><div className="card" style={{ borderColor: "#BF3437", padding: 32, textAlign: "center" }}><AlertTriangle size={36} style={{ color: "#BF3437", marginBottom: 8 }}/><div style={{ fontWeight: 800, fontSize: 18, color: "#0A0A0A" }}>{err}</div></div></PublicShell>;
  if (!data) return <PublicShell><div className="skel" style={{ height: 200 }} /></PublicShell>;

  const completed = data.patrols.filter((p) => p.status === "completada").length;
  const totalAnomalies = data.patrols.reduce((s, p) => s + (p.anomalies || 0), 0);

  return (
    <PublicShell>
      <VerifiedBanner issued_at={data.issued_at} expires_at={data.expires_at} />

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "#BF3437", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Reporte consolidado</div>
        <h1 style={{ fontSize: 32, fontWeight: 900, color: "#0A0A0A", letterSpacing: "-0.02em", margin: "0 0 4px" }}>{data.client.name}</h1>
        <div style={{ color: "var(--invict-text-muted)", fontSize: 13 }}>Período: {data.date_from} → {data.date_to}</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12, marginBottom: 22 }}>
        {[
          { label: "Servicios", value: data.services.length, icon: ClipboardList },
          { label: "Novedades", value: data.news.length, icon: FileWarning },
          { label: "Rondas", value: data.patrols.length, icon: MapPinned },
          { label: "Completadas", value: completed, icon: Check },
          { label: "Anomalías", value: totalAnomalies, icon: AlertTriangle, alert: totalAnomalies > 0 },
        ].map((m) => (
          <div key={m.label} className={`metric-card ${m.alert ? "alert" : ""}`} style={{ cursor: "default" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
              <div className="metric-label">{m.label}</div>
              <m.icon size={16} style={{ color: "#6B655A" }}/>
            </div>
            <div className="metric-value">{m.value}</div>
          </div>
        ))}
      </div>

      <h2 style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0A0A0A", margin: "0 0 10px" }}>Rondas del período</h2>
      <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 16 }}>
        {data.patrols.length === 0 && <div style={{ padding: 24, textAlign: "center", color: "var(--invict-text-muted)" }}>Sin rondas en el período.</div>}
        {data.patrols.map((p) => (
          <div key={p.id} style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1fr 1fr 1fr", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)", alignItems: "center", fontSize: 12 }}>
            <div style={{ fontWeight: 700, color: "#0A0A0A" }}><Clock size={11} style={{ display: "inline", marginRight: 4 }}/>{new Date(p.scheduled_for).toLocaleString("es-AR")}</div>
            <div>{p.vigilador_name}</div>
            <div style={{ textTransform: "uppercase", color: "var(--invict-text-muted)", fontSize: 10, letterSpacing: "0.05em" }}>{PATROL_STATUS_LABEL[p.status] || p.status}</div>
            <div>{p.points_completed}/{p.total_points}</div>
            <div style={{ color: p.anomalies > 0 ? "#BF3437" : "var(--invict-text-muted)" }}>{p.anomalies > 0 ? `${p.anomalies} anomalía(s)` : "—"}</div>
          </div>
        ))}
      </div>

      <h2 style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0A0A0A", margin: "0 0 10px" }}>Novedades del período</h2>
      <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 16 }}>
        {data.news.length === 0 && <div style={{ padding: 24, textAlign: "center", color: "var(--invict-text-muted)" }}>Sin novedades.</div>}
        {data.news.map((n) => (
          <div key={n.id} style={{ padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{n.title}</div>
              <span className={`badge ${n.severity === "critical" ? "red" : n.severity === "warning" ? "amber" : "gray"}`}>{n.severity}</span>
            </div>
            <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 4 }}>{n.supervisor_name} · {new Date(n.created_at).toLocaleString("es-AR")}</div>
          </div>
        ))}
      </div>
    </PublicShell>
  );
}

function PublicShell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: "var(--invict-bone)" }}>
      <header style={{ background: "#0A0A0A", color: "#FFFBF4", padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Brand size="md" />
        <div style={{ fontSize: 9, letterSpacing: "0.2em", color: "rgba(255,251,244,0.6)", textTransform: "uppercase", fontWeight: 700 }}>Reporte público verificable</div>
      </header>
      <main style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 20px 60px" }}>{children}</main>
      <footer style={{ padding: "20px 24px", background: "#0A0A0A", color: "rgba(255,251,244,0.5)", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, textAlign: "center" }}>
        INVICT Security Solutions · Plataforma operativa · 2026
      </footer>
    </div>
  );
}

function VerifiedBanner({ issued_at, expires_at }) {
  const issuedDate = issued_at ? new Date(issued_at * 1000).toLocaleString("es-AR") : "—";
  const expiresDate = expires_at ? new Date(expires_at * 1000).toLocaleString("es-AR") : "—";
  return (
    <div style={{ background: "#16a34a", color: "white", padding: "14px 18px", borderRadius: 4, marginBottom: 22, display: "flex", alignItems: "center", gap: 14 }} data-testid="verified-banner">
      <ShieldCheck size={28} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 800 }}>Documento verificado por INVICT</div>
        <div style={{ fontSize: 11, opacity: 0.9 }}>Emitido: {issuedDate} · Válido hasta: {expiresDate}</div>
      </div>
      <span style={{ background: "rgba(255,255,255,0.18)", padding: "4px 10px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>auténtico</span>
    </div>
  );
}
